/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HotelIcon from '@mui/icons-material/Hotel';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import AddIcon from '@mui/icons-material/Add';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { BookingSearchBar, BookingTab } from '../../components/booking';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';
import ComingSoonBadge from '../common/ComingSoonBadge';
import { PwaInstallButton } from '../PwaInstallPrompt';

// Shared styling so every primary drawer item has the same boxed-button look
const drawerOutlinedSx = {
  textTransform: 'none' as const,
  borderRadius: '24px',
  borderColor: '#2D6E6A',
  color: '#2D6E6A',
  fontWeight: 600,
};

// Demoted (quiet) styling for the Install App link — strips the filled look
const demotedInstallSx = {
  textTransform: 'none' as const,
  bgcolor: 'transparent',
  color: '#2D6E6A',
  boxShadow: 'none',
  fontWeight: 400,
  fontSize: '0.8rem',
  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline', boxShadow: 'none' },
};

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navMenuAnchor, setNavMenuAnchor] = useState<null | HTMLElement>(null);
  const [bookAnchor, setBookAnchor] = useState<null | HTMLElement>(null);
  const [bookingBarOpen, setBookingBarOpen] = useState(false);
  const [bookingTab, setBookingTab] = useState<BookingTab>('hotels');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const pwaInstallEnabled = !flagsLoading && isEnabled('pwa_install') === true;
  // Auto-close booking bar on route change
  useEffect(() => {
    setBookingBarOpen(false);
  }, [location.pathname]);

  // Check if community is enabled (at least one sub-feature must be on)
  const communityEnabled = flagsLoading || isEnabled('community_public') || isEnabled('community_friends');
  // MVP boundary: booking defaults OFF until flag confirms enabled (safe default)
  const bookingEnabled = !flagsLoading && isEnabled('booking_enabled') === true;
  const [comingSoonMessage, setComingSoonMessage] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isAuthenticated = !!localStorage.getItem('idToken');

  const isDestinationsEnabled = !flagsLoading && isEnabled('destinations');

  const navLinks = [
    { label: 'Community', href: '/community', enabled: communityEnabled },
    { label: 'Destinations', href: '/destinations', enabled: isDestinationsEnabled },
  ].filter(link => link.enabled);

  const bookMenuItems = [
    { label: 'Hotels', icon: <HotelIcon />, tab: 'hotels' as BookingTab },
    { label: 'Flights', icon: <FlightTakeoffIcon />, tab: 'flights' as BookingTab },
    { label: 'Cars', icon: <DirectionsCarIcon />, tab: 'cars' as BookingTab },
    { label: 'Activities', icon: <LocalActivityIcon />, tab: 'activities' as BookingTab },
    { label: 'divider', icon: null, tab: null },
    { label: 'Create a Trip', icon: <AddIcon />, tab: null, href: '/trips/new', allowGuest: true },
  ];

  const handleNavClick = (href: string, enabled: boolean = true) => {
    if (!enabled) return; // Don't navigate if disabled

    setMobileMenuOpen(false);
    setBookAnchor(null);

    if (href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(href);
    }
  };

  const handleLogoClick = () => {
    if (isAuthenticated) {
      navigate('/trips');
    } else {
      navigate('/');
    }
  };

  const handleBookClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!bookingEnabled) {
      setComingSoonMessage('Booking is coming soon — stay tuned!');
      return;
    }
    setBookAnchor(event.currentTarget);
  };

  const handleBookClose = () => {
    setBookAnchor(null);
  };

  const handleBookItemClick = (item: typeof bookMenuItems[0]) => {
    handleBookClose();
    setMobileMenuOpen(false);

    if (item.href) {
      // Check if this route allows guest access
      if (item.allowGuest || isAuthenticated) {
        // Allow guest access - let the page handle login prompt
        navigate(item.href);
      } else {
        // Require login first
        navigate(`/login?redirect=${encodeURIComponent(item.href)}`);
      }
    } else if (item.tab) {
      // Open booking search bar with selected tab
      setBookingTab(item.tab);
      setBookingBarOpen(true);
    }
  };

  const handleMyTripsClick = () => {
    setMobileMenuOpen(false);
    if (isAuthenticated) {
      navigate('/trips');
    } else {
      navigate('/login?redirect=/trips');
    }
  };

  const handleAuthClick = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const desktopNav = (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {isEnabled('demo_mode') && (
        <Button
          variant="outlined"
          onClick={() => navigate('/demo')}
          sx={{
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 600,
            borderRadius: '24px',
            px: 2.5,
            borderColor: '#2D6E6A',
            color: '#2D6E6A',
            '&:hover': { bgcolor: '#f0faf9', borderColor: '#1F4E4A' },
          }}
        >
          Try Demo
        </Button>
      )}
      <IconButton onClick={(e) => setNavMenuAnchor(e.currentTarget)} sx={{ color: 'inherit' }}>
        <MenuIcon />
      </IconButton>
      <Menu
        anchorEl={navMenuAnchor}
        open={Boolean(navMenuAnchor)}
        onClose={() => setNavMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 240, mt: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
      >
        {/* Community */}
        {communityEnabled && (
          <MenuItem onClick={() => { setNavMenuAnchor(null); handleNavClick('/community', true); }} sx={{ py: 1.5 }}>
            <ListItemIcon sx={{ minWidth: 36 }}><LocalActivityIcon sx={{ fontSize: 20, color: '#2D6E6A' }} /></ListItemIcon>
            <ListItemText primary="Community" />
          </MenuItem>
        )}
        {/* Sign Up — primary conversion CTA */}
        <Box sx={{ px: 2, py: 1 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => { setNavMenuAnchor(null); handleAuthClick(isEnabled('registration_open') ? '/signup' : '/early-access'); }}
            sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 700, py: 1, '&:hover': { bgcolor: '#1F4E4A' } }}
          >
            Sign Up
          </Button>
        </Box>
        {/* Log In / My Trips */}
        <MenuItem onClick={() => { setNavMenuAnchor(null); handleMyTripsClick(); }} sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}><TravelExploreIcon sx={{ fontSize: 20, color: '#2D6E6A' }} /></ListItemIcon>
          <ListItemText primary={isAuthenticated ? 'My Trips' : 'Log In / My Trips'} />
        </MenuItem>
        {/* About */}
        <MenuItem onClick={() => { setNavMenuAnchor(null); handleNavClick('/about'); }} sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}><InfoOutlinedIcon sx={{ fontSize: 20, color: '#2D6E6A' }} /></ListItemIcon>
          <ListItemText primary="About" />
        </MenuItem>
        {/* Resources */}
        <MenuItem onClick={() => { setNavMenuAnchor(null); handleNavClick('/resources'); }} sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}><ArticleOutlinedIcon sx={{ fontSize: 20, color: '#2D6E6A' }} /></ListItemIcon>
          <ListItemText primary="Resources" />
        </MenuItem>
        {/* Help / FAQ */}
        <MenuItem onClick={() => { setNavMenuAnchor(null); handleNavClick('/faq'); }} sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}><InfoOutlinedIcon sx={{ fontSize: 20, color: '#2D6E6A' }} /></ListItemIcon>
          <ListItemText primary="Help / FAQ" />
        </MenuItem>
        {/* Contact */}
        <MenuItem onClick={() => { setNavMenuAnchor(null); handleNavClick('/contact'); }} sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}><AddIcon sx={{ fontSize: 20, color: '#2D6E6A' }} /></ListItemIcon>
          <ListItemText primary="Contact" />
        </MenuItem>
        {/* Install App — gated behind 'pwa_install' flag */}
        {pwaInstallEnabled && (
          <MenuItem sx={{ py: 0.5, justifyContent: 'center' }}>
            <PwaInstallButton sx={demotedInstallSx} />
          </MenuItem>
        )}
        <Divider />
        {/* Book (Coming Soon) — gray, not clickable, dead last */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, opacity: 0.6 }}>
          <ListItemIcon sx={{ minWidth: 36 }}><HotelIcon sx={{ fontSize: 20, color: 'text.disabled' }} /></ListItemIcon>
          <Typography sx={{ color: 'text.disabled', fontWeight: 500 }}>Book</Typography>
          <ComingSoonBadge />
        </Box>
      </Menu>
    </Box>
  );

  const mobileNav = (
    <>
      <IconButton
        color="inherit"
        onClick={() => setMobileMenuOpen(true)}
        sx={{ ml: 'auto' }}
      >
        <MenuIcon />
      </IconButton>
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }} role="presentation">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => setMobileMenuOpen(false)} size="large">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Primary actions — uniform boxed buttons, in priority order */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
            {/* 1. Try Demo */}
            {isEnabled('demo_mode') && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => { setMobileMenuOpen(false); navigate('/demo'); }}
                sx={drawerOutlinedSx}
              >
                Try Demo
              </Button>
            )}

            {/* 2. Community */}
            {communityEnabled && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleNavClick('/community', true)}
                sx={drawerOutlinedSx}
              >
                Community
              </Button>
            )}

            {/* Destinations (only when flag on) — boxed to match */}
            {isDestinationsEnabled && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleNavClick('/destinations', true)}
                sx={drawerOutlinedSx}
              >
                Destinations
              </Button>
            )}

            {/* 3. Sign Up — primary conversion CTA (filled) */}
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleAuthClick(isEnabled('registration_open') ? '/signup' : '/early-access')}
              sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#1F4E4A' } }}
            >
              Sign Up
            </Button>

            {/* 4. Log In / My Trips */}
            <Button
              fullWidth
              variant="outlined"
              onClick={handleMyTripsClick}
              sx={drawerOutlinedSx}
            >
              {isAuthenticated ? 'My Trips' : 'Log In / My Trips'}
            </Button>

            {/* About */}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleNavClick('/about')}
              sx={drawerOutlinedSx}
            >
              About
            </Button>

            {/* Resources */}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleNavClick('/resources')}
              sx={drawerOutlinedSx}
            >
              Resources
            </Button>

            {/* Help / FAQ */}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleNavClick('/faq')}
              sx={drawerOutlinedSx}
            >
              Help / FAQ
            </Button>

            {/* 5. Contact */}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleNavClick('/contact')}
              sx={drawerOutlinedSx}
            >
              Contact
            </Button>

            {/* 6. Install App — gated behind 'pwa_install' flag */}
            {pwaInstallEnabled && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                <PwaInstallButton sx={demotedInstallSx} />
              </Box>
            )}
          </Box>

          {/* 7. Book (Coming Soon) — gray, not clickable, dead last */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', px: 1, py: 1, opacity: 0.6 }}>
            <Typography sx={{ color: 'text.disabled', fontWeight: 500, fontSize: '0.95rem' }}>
              Book
            </Typography>
            <ComingSoonBadge />
          </Box>
        </Box>
      </Drawer>
    </>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          background: scrolled
            ? 'rgba(255, 255, 255, 0.95)'
            : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: scrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none',
          color: 'text.primary',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: 0 }}>
            {/* Logo */}
            <Box
              onClick={handleLogoClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
              }}
            >
              <TravelExploreIcon
                sx={{
                  fontSize: 32,
                  color: 'primary.main',
                  mr: 1,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: 'primary.main',
                  letterSpacing: '-0.5px',
                }}
              >
                PERCORO
              </Typography>
            </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isMobile ? mobileNav : desktopNav}

            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Booking Search Bar */}
      <BookingSearchBar
        open={bookingBarOpen}
        initialTab={bookingTab}
        onClose={() => setBookingBarOpen(false)}
      />
      <Snackbar
        open={!!comingSoonMessage}
        autoHideDuration={4000}
        onClose={() => setComingSoonMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setComingSoonMessage('')}
          severity="info"
          sx={{ width: '100%' }}
        >
          {comingSoonMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Navbar;
